import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem("photo-cart");

      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Could not load cart:", error);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("photo-cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems((currentItems) => {
      const existingIndex = currentItems.findIndex(
        (existing) =>
          String(existing.printId) === String(item.printId) &&
          existing.size === item.size &&
          existing.paper === item.paper &&
          existing.frame === item.frame,
      );

      // Same print + same options = increase quantity
      if (existingIndex !== -1) {
        return currentItems.map((existing, index) =>
          index === existingIndex
            ? {
                ...existing,
                quantity: existing.quantity + 1,
              }
            : existing,
        );
      }

      // New configuration
      return [
        ...currentItems,
        {
          ...item,
          quantity: 1,
        },
      ];
    });
  };

  const removeFromCart = (indexToRemove) => {
    setCartItems((currentItems) =>
      currentItems.filter((_, index) => index !== indexToRemove),
    );
  };

  const updateQuantity = (indexToUpdate, quantity) => {
    if (quantity < 1) {
      setCartItems((currentItems) =>
        currentItems.filter((_, index) => index !== indexToUpdate),
      );

      return;
    }

    setCartItems((currentItems) =>
      currentItems.map((item, index) =>
        index === indexToUpdate
          ? {
              ...item,
              quantity,
            }
          : item,
      ),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  const cartTotal = useMemo(() => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  }, [cartItems]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        cartTotal,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
